/**
 * API client for the Cloudflare Worker backend.
 * All requests carry the Telegram initData in the Authorization header.
 */

import type { Member, CreateTaskRequest, CreateTaskResponse } from './types';

/** Base URL of the Cloudflare Worker. Injected via env at build time. */
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function getInitData(): string {
  return window.Telegram?.WebApp?.initData ?? '';
}

function authHeader(): Record<string, string> {
  const initData = getInitData();
  if (!initData) {
    // During local dev outside Telegram the header will be invalid;
    // the backend will return 401 which surfaces as an error in the UI.
    console.warn('[mini-app] WebApp.initData is empty — running outside Telegram?');
  }
  return { Authorization: `tma ${initData}` };
}

export async function fetchMembers(listId: string): Promise<Member[]> {
  const res = await fetch(
    `${API_BASE}/api/members?list_id=${encodeURIComponent(listId)}`,
    { headers: authHeader() },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  const data = await res.json() as { ok: boolean; members: Member[] };
  return data.members;
}

export async function submitTask(payload: CreateTaskRequest): Promise<CreateTaskResponse> {
  const res = await fetch(`${API_BASE}/api/tasks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json() as CreateTaskResponse;
  if (!res.ok) {
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return data;
}
